import { addDisplayName, addHandlers, addProps, addRef, flowMax } from 'ad-hok'
import {
  ColDef,
  FilterChangedEvent,
  RowClickedEvent,
  ValueSetterParams,
} from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import cx from 'classnames'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { CreatorEarnings, CreatorEarningsManyInput } from 'shared/src/api/types'
import { addNavigate, addPathParam } from 'shared/src/utils/addNavigate'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { typedAs } from 'shared/src/utils/typedAs'

import { Button } from 'components/Button'
import { Page } from 'components/Page'
import { addToastContext } from 'components/toastContext'
import { addCmsLoadingAndErrorIndicators } from 'utils/addCmsLoadingAndErrorIndicators'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { makeUserEarningsRoute } from 'utils/routes'
import {
  COLUMN_TYPES,
  DEFAULT_COL_DEF,
  DOLLAR_AMOUNT_COLUMN_WIDTH,
  EDITABLE_CELL_BACKGROUND_COLOR,
  EDITABLE_PARTNERSHIP_CELL_BACKGROUND_COLOR,
  EDITED_CELL_BACKGROUND_COLOR,
  displayedEarnings,
  formatNumber,
  formatPrice,
  getAggregateRow,
  getEditableCellStyle,
  getIsEditable,
  onFilterChanged,
  totalEarnings,
} from './utils'

const useStyles = createUseStyles({
  buttons: {
    display: 'flex',
    gap: 20,
    marginBottom: 20,
  },
  table: {
    marginTop: 20,
    width: '100%',
    height: '90dvh',
  },
})

export const EarningsDay: FC = flowMax(
  addDisplayName('EarningsDay'),
  addPathParam('date'),
  addApi,
  addDataLoadingFromPromise(
    'creatorEarnings',
    ({ date, api }) => api.creatorEarnings.getForDate(date),
    []
  ),
  addCmsLoadingAndErrorIndicators('creatorEarnings'),
  addProps(({ creatorEarnings }) => ({
    originalCreatorEarnings: structuredClone(creatorEarnings),
  })),
  addTranslationHelpers,
  addProps(({ t }) => ({
    columns: typedAs<ColDef<CreatorEarnings>[]>([
      {
        headerName: t('admin.earnings.columns.username'),
        field: 'username',
      },
      {
        headerName: t('admin.earnings.columns.totalEarnings'),
        type: ['number'],
        valueGetter: ({ data }) => data && totalEarnings(data),
        valueFormatter: formatPrice,
        sort: 'desc',
      },
      {
        headerName: t('admin.earnings.columns.mediaViews'),
        field: 'mediaViews',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.mediaViewEarnings'),
        editable: (params) => getIsEditable(params),
        type: ['number'],
        valueGetter: ({ data }) =>
          data && displayedEarnings(data, 'mediaViews'),
        valueSetter: (params: ValueSetterParams) => {
          params.data.updatedMediaViewEarnings = isNaN(params.newValue)
            ? 0
            : params.newValue
          return true
        },
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
        valueFormatter: formatPrice,
        cellStyle: (params) => getEditableCellStyle(params, 'mediaViews'),
      },
      {
        headerName: t('admin.earnings.columns.productViews'),
        field: 'productViews',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.productViewEarnings'),
        editable: (params) => getIsEditable(params),
        type: ['number'],
        valueGetter: ({ data }) =>
          data && displayedEarnings(data, 'productViews'),
        valueSetter: (params: ValueSetterParams) => {
          params.data.updatedProductViewEarnings = isNaN(params.newValue)
            ? 0
            : params.newValue
          return true
        },
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
        cellStyle: (params) => getEditableCellStyle(params, 'productViews'),
      },
      {
        headerName: t('admin.earnings.columns.outgoingProductClicks'),
        field: 'outgoingProductClicks',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.outgoingProductClickEarnings'),
        editable: (params) => getIsEditable(params),
        type: ['number'],
        valueGetter: ({ data }) =>
          data && displayedEarnings(data, 'outgoingProductClicks'),
        valueSetter: (params: ValueSetterParams) => {
          params.data.updatedOutgoingProductClickEarnings = isNaN(
            params.newValue
          )
            ? 0
            : params.newValue
          return true
        },
        valueFormatter: formatPrice,
        cellStyle: (params) =>
          getEditableCellStyle(params, 'outgoingProductClicks'),
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.mediaPublished'),
        field: 'mediaPublished',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.mediaPublishedEarnings'),
        editable: (params) => getIsEditable(params),
        hide: true,
        type: ['number'],
        valueGetter: ({ data }) =>
          data && displayedEarnings(data, 'mediaPublished'),
        valueSetter: (params: ValueSetterParams) => {
          params.data.updatedMediaPublishedEarnings = isNaN(params.newValue)
            ? 0
            : params.newValue
          return true
        },
        valueFormatter: formatPrice,
        cellStyle: (params) => getEditableCellStyle(params, 'mediaPublished'),
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.mediaTagged'),
        field: 'mediaTagged',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.mediaTaggedEarnings'),
        editable: (params) => getIsEditable(params),
        hide: true,
        type: ['number'],
        valueGetter: ({ data }) =>
          data && displayedEarnings(data, 'mediaTagged'),
        valueSetter: (params: ValueSetterParams) => {
          params.data.updatedMediaTaggedEarnings = isNaN(params.newValue)
            ? 0
            : params.newValue
          return true
        },
        valueFormatter: formatPrice,
        cellStyle: (params) => getEditableCellStyle(params, 'mediaTagged'),
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.editsCreated'),
        field: 'editsCreated',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.editsCreatedEarnings'),
        editable: (params) => getIsEditable(params),
        hide: true,
        type: ['number'],
        valueGetter: ({ data }) =>
          data && displayedEarnings(data, 'editsCreated'),
        valueSetter: (params: ValueSetterParams) => {
          params.data.updatedEditsCreatedEarnings = isNaN(params.newValue)
            ? 0
            : params.newValue
          return true
        },
        valueFormatter: formatPrice,
        cellStyle: (params) => getEditableCellStyle(params, 'editsCreated'),
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.bonusEarnings'),
        field: 'bonusEarnings',
        editable: (params) => getIsEditable(params),
        type: ['number'],
        valueSetter: (params: ValueSetterParams) => {
          params.data.bonusEarnings = isNaN(params.newValue)
            ? 0
            : params.newValue
          return true
        },
        valueFormatter: formatPrice,
        cellStyle: (params) =>
          params.node.isRowPinned()
            ? null
            : params.data && !!params.data.bonusEarnings
            ? { backgroundColor: EDITED_CELL_BACKGROUND_COLOR }
            : params.data?.isCommitted
            ? null
            : { backgroundColor: EDITABLE_CELL_BACKGROUND_COLOR },
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.partnershipEarnings'),
        field: 'partnershipEarnings',
        editable: (params) => getIsEditable(params),
        type: ['number'],
        valueSetter: (params: ValueSetterParams) => {
          params.data.partnershipEarnings = isNaN(params.newValue)
            ? 0
            : params.newValue
          return true
        },
        valueFormatter: formatPrice,
        cellStyle: (params) =>
          params.node.isRowPinned()
            ? null
            : params.data && !!params.data.partnershipEarnings
            ? { backgroundColor: EDITED_CELL_BACKGROUND_COLOR }
            : params.data?.isCommitted
            ? null
            : { backgroundColor: EDITABLE_PARTNERSHIP_CELL_BACKGROUND_COLOR },
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
    ]),
  })),
  addRef('gridRef', typedAs<AgGridReact | null>(null)),
  addNavigate,
  addToastContext,
  addHandlers({
    onSelect:
      ({ navigate }) =>
      (event: RowClickedEvent) => {
        if (event.data && event.data.userId) {
          navigate(makeUserEarningsRoute(event.data.userId))
        }
      },
    onSave:
      ({
        originalCreatorEarnings,
        creatorEarnings,
        api,
        showSuccessToast,
        t,
      }) =>
      () => {
        const toUpdate: CreatorEarningsManyInput = creatorEarnings.flatMap(
          (e, i) => {
            if (
              !(
                e.updatedMediaViewEarnings ===
                  originalCreatorEarnings[i].updatedMediaViewEarnings &&
                e.updatedProductViewEarnings ===
                  originalCreatorEarnings[i].updatedProductViewEarnings &&
                e.updatedOutgoingProductClickEarnings ===
                  originalCreatorEarnings[i]
                    .updatedOutgoingProductClickEarnings &&
                e.updatedMediaPublishedEarnings ===
                  originalCreatorEarnings[i].updatedMediaPublishedEarnings &&
                e.updatedMediaTaggedEarnings ===
                  originalCreatorEarnings[i].updatedMediaTaggedEarnings &&
                e.updatedEditsCreatedEarnings ===
                  originalCreatorEarnings[i].updatedEditsCreatedEarnings &&
                e.bonusEarnings === originalCreatorEarnings[i].bonusEarnings &&
                e.partnershipEarnings ===
                  originalCreatorEarnings[i].partnershipEarnings
              )
            ) {
              return {
                id: e.id,
                updatedMediaViewEarnings: e.updatedMediaViewEarnings,
                updatedProductViewEarnings: e.updatedProductViewEarnings,
                updatedOutgoingProductClickEarnings:
                  e.updatedOutgoingProductClickEarnings,
                updatedMediaPublishedEarnings: e.updatedMediaPublishedEarnings,
                updatedMediaTaggedEarnings: e.updatedMediaTaggedEarnings,
                updatedEditsCreatedEarnings: e.updatedEditsCreatedEarnings,
                bonusEarnings: e.bonusEarnings,
                partnershipEarnings: e.partnershipEarnings,
              }
            }
            return []
          }
        )
        api.creatorEarnings.updateManyUpdatedEarnings(toUpdate).then(() =>
          showSuccessToast({
            title: t('admin.earnings.saveUpdatedEarningsTitle'),
            details: t('admin.earnings.saveUpdatedEarningsDetails'),
          })
        )
      },
    onReset:
      ({ api, date }) =>
      () => {
        api.creatorEarnings.reset({ date }).then(() => window.location.reload())
      },
    onGridReady:
      ({ creatorEarnings, gridRef }) =>
      () => {
        gridRef.current?.api?.setPinnedTopRowData(
          getAggregateRow(creatorEarnings)
        )
      },
    onCellValueChanged:
      ({ creatorEarnings, gridRef }) =>
      () => {
        gridRef.current?.api?.setPinnedTopRowData(
          getAggregateRow(creatorEarnings)
        )
      },
    onFilterChanged:
      ({ creatorEarnings }) =>
      (event: FilterChangedEvent) => {
        onFilterChanged(event, creatorEarnings)
      },
    onExport:
      ({ date, gridRef, t }) =>
      () => {
        gridRef.current!.api.exportDataAsCsv({
          fileName: t('admin.earnings.export.day', { date }),
        })
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    date,
    creatorEarnings,
    onGridReady,
    onCellValueChanged,
    columns,
    gridRef,
    onSelect,
    onSave,
    onReset,
    onExport,
    onFilterChanged,
    classes,
    t,
  }) => (
    <Page leftButton="">
      <h1>{date}</h1>
      <div className={classes.buttons}>
        <Button onClick={onSave} text={t('admin.earnings.save')} />
        <Button onClick={onReset} text={t('admin.earnings.reset')} />
        <Button onClick={onExport} text={t('admin.earnings.export.button')} />
      </div>
      <div className={cx('ag-theme-alpine', classes.table)}>
        <AgGridReact
          ref={gridRef}
          columnDefs={columns}
          columnTypes={COLUMN_TYPES}
          defaultColDef={DEFAULT_COL_DEF}
          rowData={creatorEarnings}
          onRowDoubleClicked={onSelect}
          undoRedoCellEditing={true}
          onCellValueChanged={onCellValueChanged}
          onGridReady={onGridReady}
          onFilterChanged={onFilterChanged}
        />
      </div>
    </Page>
  )
)
